import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ShieldAlert, Pin, Lock, Trash2, CheckCircle2, RefreshCw, Flag, User, Clock,
} from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function KPI({ label, value, testid }) {
  return (
    <Card className="p-4 border-white/10 bg-card" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-2xl font-semibold text-white mt-1 font-display">{value}</div>
    </Card>
  );
}

export default function ForumModerationPage() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/forum/reports");
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pin = async (tid) => {
    try { await api.post(`/admin/forum/threads/${tid}/pin`); toast.success("Pin toggled"); load(); }
    catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };
  const lock = async (tid) => {
    try { await api.post(`/admin/forum/threads/${tid}/lock`); toast.success("Lock toggled"); load(); }
    catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };
  const delThread = async (tid) => {
    if (!window.confirm("Delete this thread?")) return;
    try { await api.delete(`/admin/forum/threads/${tid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };
  const delPost = async (pid) => {
    if (!window.confirm("Delete this post?")) return;
    try { await api.delete(`/admin/forum/posts/${pid}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };
  const clearThreadReports = async (tid) => {
    try { await api.post(`/admin/forum/threads/${tid}/clear-reports`); toast.success("Reports dismissed"); load(); }
    catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };
  const clearPostReports = async (pid) => {
    try { await api.post(`/admin/forum/posts/${pid}/clear-reports`); toast.success("Reports dismissed"); load(); }
    catch (e) { toast.error(apiErrorMessage(e) || "Failed"); }
  };

  const k = data?.kpis || {};

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Community"
        title="Forum Moderation"
        subtitle="Reports queue — pin, lock, dismiss, or remove threads and posts."
        right={<Button variant="outline" className="border-white/10" onClick={load} data-testid="mod-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>}
      />

      <div className="grid grid-cols-3 gap-3">
        <KPI label="Reported threads" value={k.reported_threads || 0} testid="mod-kpi-threads" />
        <KPI label="Reported posts" value={k.reported_posts || 0} testid="mod-kpi-posts" />
        <KPI label="Total open reports" value={k.total || 0} testid="mod-kpi-total" />
      </div>

      {/* Threads */}
      <Card className="p-4 border-white/10 bg-card">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="h-4 w-4 text-red-400" />
          <div className="text-sm font-semibold text-white">Reported threads</div>
        </div>
        {(data?.threads || []).length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center" data-testid="no-reported-threads">
            No reported threads right now — the community is playing nice.
          </div>
        ) : (
          <div className="space-y-3">
            {data.threads.map((t) => (
              <Card key={t.id} className="p-3 border-red-500/20 bg-red-500/[0.02]" data-testid={`mod-thread-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {t.pinned && <Pin className="h-3 w-3 text-fuchsia-300" />}
                      {t.locked && <Lock className="h-3 w-3 text-red-300" />}
                      <Badge variant="outline" className="border-red-500/50 text-red-300">
                        <Flag className="h-3 w-3 mr-0.5" /> {t.reports.length} report{t.reports.length === 1 ? "" : "s"}
                      </Badge>
                    </div>
                    <div className="text-sm font-semibold text-white">{t.title}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      <User className="h-3 w-3 inline mr-1" />{t.author_name} · <Clock className="h-3 w-3 inline ml-1 mr-1" />{relTime(t.created_at)}
                    </div>
                    <div className="text-xs text-zinc-300 mt-2 line-clamp-2 whitespace-pre-wrap">{t.body}</div>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Reasons</div>
                  <ul className="text-[11px] text-zinc-300 space-y-0.5">
                    {t.reports.map((r, i) => (
                      <li key={i}>• {r.reporter_name}: <span className="text-zinc-400">{r.reason}</span></li>
                    ))}
                  </ul>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="border-white/10 h-7 px-2 text-[11px]"
                          onClick={() => pin(t.id)} data-testid={`pin-${t.id}`}>
                    <Pin className="h-3 w-3 mr-1" /> {t.pinned ? "Unpin" : "Pin"}
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-500/30 text-red-300 h-7 px-2 text-[11px]"
                          onClick={() => lock(t.id)} data-testid={`lock-${t.id}`}>
                    <Lock className="h-3 w-3 mr-1" /> {t.locked ? "Unlock" : "Lock"}
                  </Button>
                  <Button size="sm" variant="outline" className="border-emerald-500/30 text-emerald-300 h-7 px-2 text-[11px]"
                          onClick={() => clearThreadReports(t.id)} data-testid={`dismiss-thread-${t.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Dismiss reports
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 h-7 px-2 text-[11px]"
                          onClick={() => delThread(t.id)} data-testid={`delete-thread-${t.id}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Posts */}
      <Card className="p-4 border-white/10 bg-card">
        <div className="flex items-center gap-2 mb-3">
          <ShieldAlert className="h-4 w-4 text-red-400" />
          <div className="text-sm font-semibold text-white">Reported posts</div>
        </div>
        {(data?.posts || []).length === 0 ? (
          <div className="text-sm text-zinc-500 py-6 text-center" data-testid="no-reported-posts">
            No reported posts.
          </div>
        ) : (
          <div className="space-y-3">
            {data.posts.map((p) => (
              <Card key={p.id} className="p-3 border-red-500/20 bg-red-500/[0.02]" data-testid={`mod-post-${p.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="border-red-500/50 text-red-300">
                    <Flag className="h-3 w-3 mr-0.5" /> {p.reports.length}
                  </Badge>
                  <span className="text-[11px] text-zinc-500">
                    <User className="h-3 w-3 inline mr-1" />{p.author_name}
                    <Clock className="h-3 w-3 inline ml-2 mr-1" />{relTime(p.created_at)}
                  </span>
                </div>
                <div className="text-xs text-zinc-300 mt-1 whitespace-pre-wrap line-clamp-3">{p.body}</div>
                <div className="mt-2 pt-2 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Reasons</div>
                  <ul className="text-[11px] text-zinc-300 space-y-0.5">
                    {p.reports.map((r, i) => (
                      <li key={i}>• {r.reporter_name}: <span className="text-zinc-400">{r.reason}</span></li>
                    ))}
                  </ul>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="border-emerald-500/30 text-emerald-300 h-7 px-2 text-[11px]"
                          onClick={() => clearPostReports(p.id)} data-testid={`dismiss-post-${p.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Dismiss
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-500/40 text-red-300 h-7 px-2 text-[11px]"
                          onClick={() => delPost(p.id)} data-testid={`delete-post-${p.id}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Delete post
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
